import { useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Card, CardActionArea, Chip, MenuItem, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { DataGridPremium, type GridColDef, type GridRowParams } from '@mui/x-data-grid-premium';
import { useQuery } from '@tanstack/react-query';
import { teamActivityService, type TeamActivityRow, type TeamActivityWindow } from '../../services/team-activity.service';
import { useAuthStore } from '../../stores/auth.store';
import { useWorkspaceStore } from '../../stores/workspace.store';
import { useGridHeight } from '../../hooks/useGridHeight';
import { useUrlState, codecs } from '../../hooks/useUrlState';
import { DATE_PRESETS, isDatePresetKey, resolveDatePreset, type DatePresetKey } from '../../utils/date-presets';
import { dayRangeParams } from '../../utils/timezone';
import ActivityHeatmap from './ActivityHeatmap';
import { AREAS, DEVICES, ago, areaLabel, deviceLabel, fmtDuration, isOnline, teamToday, utc } from './team-activity.format';

function SplitBar({ parts, list, label }: { parts: Record<string, number>; list: { key: string; color: string }[]; label: (k: string) => string }) {
  const total = Object.values(parts).reduce((a, b) => a + b, 0);
  if (!total) return <Typography variant="caption" color="text.disabled">—</Typography>;
  const tip = list.filter((l) => parts[l.key]).map((l) => `${label(l.key)} ${Math.round((parts[l.key] / total) * 100)}%`).join(' · ');
  return (
    <Tooltip title={tip}>
      <Box sx={{ display: 'flex', width: '100%', height: 8, borderRadius: 1, overflow: 'hidden' }}>
        {list.filter((l) => parts[l.key]).map((l) => (
          <Box key={l.key} sx={{ width: `${(parts[l.key] / total) * 100}%`, bgcolor: l.color }} />
        ))}
      </Box>
    </Tooltip>
  );
}

/** This period against the average of the four before it. */
function Trend({ row }: { row: TeamActivityRow }) {
  if (!row.baseline_minutes) return null;
  const change = (row.active_minutes - row.baseline_minutes) / row.baseline_minutes;
  if (Math.abs(change) < 0.1) return null;
  return (
    <Typography component="span" variant="caption" sx={{ color: change > 0 ? 'success.main' : 'error.main', ml: 0.75 }}>
      {change > 0 ? '▲' : '▼'} {Math.round(Math.abs(change) * 100)}%
    </Typography>
  );
}

const Online = ({ row }: { row: TeamActivityRow }) => (isOnline(row.last_active)
  ? <Box component="span" sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', mr: 0.75 }} />
  : null);

const num = (field: keyof TeamActivityRow, headerName: string, description?: string): GridColDef<TeamActivityRow> => ({
  field, headerName, description, type: 'number', width: 92, headerAlign: 'right', align: 'right',
});

const COLUMNS: GridColDef<TeamActivityRow>[] = [
  {
    field: 'name', headerName: 'User', flex: 1, minWidth: 190,
    renderCell: ({ row }) => (
      <Box sx={{ lineHeight: 1.25, py: 0.5 }}>
        <Typography variant="body2" fontWeight={600}><Online row={row} />{row.name}</Typography>
        <Typography variant="caption" color="text.secondary">{row.email}</Typography>
      </Box>
    ),
  },
  {
    field: 'active_minutes', headerName: 'Active time', type: 'number', width: 140, headerAlign: 'right', align: 'right',
    description: 'Minutes with the app in front of them and in use. Several tabs or devices count once. The arrow compares the four periods before.',
    renderCell: ({ row }) => <span>{fmtDuration(row.active_minutes)}<Trend row={row} /></span>,
  },
  num('days_active', 'Days'),
  num('sessions', 'Sessions', 'A pause of more than 10 minutes, or midnight, ends a session'),
  {
    field: 'by_area', headerName: 'Areas', width: 130, sortable: false,
    renderCell: ({ row }) => <SplitBar parts={row.by_area} list={AREAS} label={areaLabel} />,
  },
  {
    field: 'by_device', headerName: 'Devices', width: 110, sortable: false,
    renderCell: ({ row }) => <SplitBar parts={row.by_device} list={DEVICES} label={deviceLabel} />,
  },
  num('writes', 'Changes', 'Edits made in the app'),
  {
    field: 'mcp', headerName: 'MCP', width: 110, headerAlign: 'right', align: 'right',
    description: 'Calls through MCP: reads / writes',
    valueGetter: (_v, row) => row.mcp_reads + row.mcp_writes,
    renderCell: ({ row }) => (row.mcp_reads + row.mcp_writes ? `${row.mcp_reads} / ${row.mcp_writes}` : '—'),
  },
  {
    field: 'last_active', headerName: 'Last active', width: 130,
    valueGetter: (v: string | null) => (v ? utc(v).getTime() : 0),
    valueFormatter: (_v: number, row) => (isOnline(row.last_active) ? 'Online now' : ago(row.last_active)),
  },
];

export default function TeamActivityPage() {
  const navigate = useNavigate();
  const isAdmin = useAuthStore((s) => s.user?.role) === 'admin';
  const [preset, setPreset] = useUrlState<string>('range', 'last_7_days', codecs.str);
  const [view, setView] = useUrlState<string>('view', 'table', codecs.str);
  const range = (isDatePresetKey(preset) ? preset : 'last_7_days') as DatePresetKey;
  const ws = useWorkspaceStore((s) => s.selectedIds.join(','));
  const win: TeamActivityWindow = useMemo(() => (dayRangeParams(resolveDatePreset(range, teamToday()))), [range]);
  const filterRef = useRef<HTMLDivElement>(null);
  const gridHeight = useGridHeight(filterRef);
  const open = (id: string) => navigate(`/team-activity/${id}?range=${range}`);

  const { data, isLoading, error } = useQuery({
    queryKey: ['team-activity', win, ws],
    queryFn: () => teamActivityService.summary(win),
    enabled: isAdmin,
  });
  const rows = useMemo(() => [...(data ?? [])].sort((a, b) => b.active_minutes - a.active_minutes), [data]);
  const online = rows.filter((r) => isOnline(r.last_active)).length;

  if (!isAdmin) {
    return (
      <Box>
        <Typography variant="h4" fontWeight={600} sx={{ mb: 3 }}>Team activity</Typography>
        <Alert severity="warning">You do not have access to this page.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box ref={filterRef} sx={{ mb: 2, display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography variant="h4" fontWeight={600}>Team activity</Typography>
          <Typography variant="body2" color="text.secondary" component="div">
            When people work, where in the app, on what device, and through MCP.
            {online > 0 && <Chip size="small" color="success" variant="outlined" label={`${online} online now`} sx={{ ml: 1 }} />}
          </Typography>
        </Box>
        <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, v) => v && setView(v)}>
          <ToggleButton value="table">Table</ToggleButton>
          <ToggleButton value="heatmaps">Heatmaps</ToggleButton>
        </ToggleButtonGroup>
        <TextField select size="small" label="Period (Israel time)" value={range} onChange={(e) => setPreset(e.target.value)} sx={{ minWidth: 170 }}>
          {DATE_PRESETS.map((p) => <MenuItem key={p.key} value={p.key}>{p.title}</MenuItem>)}
        </TextField>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>Could not load team activity.</Alert>}
      {view === 'heatmaps' ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 1.5 }}>
          {rows.map((r) => (
            <Card key={r.user_id} variant="outlined">
              <CardActionArea onClick={() => open(r.user_id)} sx={{ p: 1.5 }}>
                <Typography variant="body2" fontWeight={600} noWrap><Online row={r} />{r.name}</Typography>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                  {fmtDuration(r.active_minutes)} · {r.days_active} days{r.mcp_reads + r.mcp_writes ? ` · ${r.mcp_reads + r.mcp_writes} MCP` : ''}
                  <Trend row={r} />
                </Typography>
                <ActivityHeatmap values={r.heatmap} compact />
              </CardActionArea>
            </Card>
          ))}
        </Box>
      ) : (
        <DataGridPremium
          rows={rows}
          columns={COLUMNS}
          getRowId={(r: TeamActivityRow) => r.user_id}
          loading={isLoading}
          density="compact"
          getRowHeight={() => 'auto'}
          onRowClick={({ row }: GridRowParams<TeamActivityRow>) => open(row.user_id)}
          disableRowSelectionOnClick
          hideFooter
          sx={{ height: gridHeight, '& .MuiDataGrid-row': { cursor: 'pointer' }, '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' } }}
        />
      )}
    </Box>
  );
}
